import datapoints from './datapoints';
import createItemContextsForDeleteStoreUpdate from './helpers/createItemContextsForDeleteStoreUpdate';
import showNotificationOnSchedule from './helpers/showNotificationOnSchedule';
import scheduleItemsService from '../services/scheduleItemsService';
import { OwaDate } from 'owa-datetime';
import { getRowIdsFromRowKeys, TableView } from 'owa-mail-list-store';
import getItemIdsFromConversations from 'owa-mail-list-store/lib/utils/getItemIdsFromConversations';
import { actionNames, DeleteConversationStoreUpdate } from 'owa-mail-store-actions';
import * as undoActions from 'owa-mail-undo';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { raiseAction } from 'satcheljs/lib/legacy/stitch';
import deleteItemsStoreUpdate, {
    ItemContext,
} from 'owa-mail-actions/lib/triage/deleteItemsStoreUpdate';

/**
 * Schedule or Unschedule items
 * @param rowKeys the row keys
 * @param tableView the tableView
 * @param actionSouce the actionSource
 * @param scheduleReturnTime the time item will return or undefined to unschedule
 */
export default createLegacyActionWithDatapoint('scheduleItems', datapoints.scheduleItems)(
    async function scheduleItems(
        rowKeys: string[],
        tableView: TableView,
        actionSource: ActionSource,
        scheduleReturnTime: OwaDate | undefined,
        isScheduled?: boolean
    ): Promise<void> {
        // Get the dedupped item ids to update, as multiple rowKeys could map to the same rowId
        let itemIdsToSchedule: string[];
        switch (tableView.tableQuery.listViewType) {
            case ReactListViewType.Conversation:
                itemIdsToSchedule = getItemIdsFromConversations(rowKeys, tableView.id);
                break;

            case ReactListViewType.Message:
                itemIdsToSchedule = getRowIdsFromRowKeys(rowKeys, tableView.id);
                break;
        }

        if (itemIdsToSchedule.length === 0) {
            // No need to send out the service call
            return Promise.resolve();
        }

        // Optimistically delete items on client.
        // *ONLY* if we are not in the scheduled folder.
        const folderId = tableView.tableQuery.folderId;
        const isScheduledFolder = folderIdToName(folderId) === 'scheduled';
        if (!isScheduledFolder) {
            switch (tableView.tableQuery.listViewType) {
                case ReactListViewType.Message:
                    const itemContexts: ItemContext[] = createItemContextsForDeleteStoreUpdate(
                        itemIdsToSchedule
                    );
                    deleteItemsStoreUpdate(itemContexts, folderId);
                    break;
                case ReactListViewType.Conversation:
                    const conversationIdStrings = getRowIdsFromRowKeys(rowKeys, tableView.id);
                    raiseAction<DeleteConversationStoreUpdate>(
                        actionNames.DeleteConversationStoreUpdate
                    )(conversationIdStrings, tableView);
                    break;
            }
        }

        undoActions.clearLastUndoableAction();
        const scheduleItemsPromise = scheduleItemsService(itemIdsToSchedule, scheduleReturnTime);

        // DO not allow undo within scheduled as we have no way to recover ReturnTime
        if (!isScheduledFolder) {
            undoActions.addActionToUndoStack(async () => {
                let itemIdsToUndo: string[] = [];
                const responses = await scheduleItemsPromise;
                for (const response of responses) {
                    if (response.ResponseClass === 'Success') {
                        itemIdsToUndo.push(response.Item.ItemId.Id);
                    }
                }

                if (itemIdsToUndo.length > 0) {
                    // Perform unschedule
                    await scheduleItemsService(itemIdsToUndo);
                }
                return Promise.resolve();
            }, tableView.serverFolderId);
        }

        showNotificationOnSchedule(!isScheduledFolder, scheduleReturnTime);

        return scheduleItemsPromise
            .then(() => {
                return Promise.resolve();
            })
            .catch(error => {
                return Promise.reject(error);
            });
    }
);
